"use client";

import React, { useRef, useState, useEffect } from "react";
import {
  Grid,
  Card,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  Avatar,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockResetIcon from "@mui/icons-material/LockReset";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const UserInfo = ({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  phoneNumber,
  setPhoneNumber,
  password,
  setPassword,
  role,
  setRole,
  profilePicture,
  setProfilePicture,
  isEdit = false,
  onValidationChange
}) => {
  const fileRef = useRef(null);
  const [logoName, setLogoName] = useState("");
  const [logo, setLogo] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleClickTogglePassword = () => setShowPassword(!showPassword);

  // ✅ Password Modal State
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [tempConfirmPassword, setTempConfirmPassword] = useState("");
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [showTempConfirmPassword, setShowTempConfirmPassword] = useState(false);

  const handleOpenPasswordModal = () => {
    setTempPassword("");
    setTempConfirmPassword("");
    setOpenPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setOpenPasswordModal(false);
  };

  const handleConfirmPasswordChange = () => {
    setPassword(tempPassword);
    setOpenPasswordModal(false);
  };

  const isPasswordMatch = tempPassword === tempConfirmPassword;
  const isPasswordLengthValid = tempPassword.length >= 6;
  const isPasswordChangeValid = isPasswordMatch && isPasswordLengthValid;

  // ✅ Validation states
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    role: ""
  });

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false,
    password: false,
    role: false
  });

  useEffect(() => {
    let objectUrl;
    setImageError(false);

    if (!profilePicture) {
      setLogo(null);
      setLogoName("");
      return;
    }
    if (typeof profilePicture === "string") {
      setLogo(profilePicture);
      try {
        const last = profilePicture.split("/").pop();
        if (last) setLogoName(last);
      } catch {
        setLogoName("");
      }
    } else if (profilePicture instanceof File) {
      objectUrl = URL.createObjectURL(profilePicture);
      setLogo(objectUrl);
      setLogoName(profilePicture.name || "selected-image");
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [profilePicture]);

  // ✅ Validation functions
  const validateFirstName = (value) => {
    if (!value.trim()) return "First name is required";
    if (value.trim().length < 2) return "First name must be at least 2 characters";
    if (value.trim().length > 50) return "First name must be less than 50 characters";
    if (!/^[a-zA-Z\s'-]+$/.test(value.trim())) return "First name can only contain letters, spaces, hyphens, and apostrophes";
    return "";
  };

  const validateLastName = (value) => {
    if (!value.trim()) return "Last name is required";
    if (value.trim().length < 2) return "Last name must be at least 2 characters";
    if (value.trim().length > 50) return "Last name must be less than 50 characters";
    if (!/^[a-zA-Z\s'-]+$/.test(value.trim())) return "Last name can only contain letters, spaces, hyphens, and apostrophes";
    return "";
  };

  const validateEmail = (value) => {
    if (!value.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) return "Please enter a valid email address";
    if (value.trim().length > 100) return "Email must be less than 100 characters";
    return "";
  };

  const validatePhoneNumber = (value) => {
    if (!value.trim()) return "Phone number is required";
    if (!/^[\d\s\-\+\(\)]+$/.test(value.trim())) return "Phone number can only contain digits, spaces, hyphens, and parentheses";
    if (value.trim().length < 10) return "Phone number must be at least 10 characters";
    if (value.trim().length > 20) return "Phone number must be less than 20 characters";
    return "";
  };

  const validatePassword = (value) => {
    if (!value) return "";
    if (value.length < 6) return "Password must be at least 6 characters";
    if (value.length > 50) return "Password must be less than 50 characters";
    return "";
  };

  const validateRole = (value) => {
    if (!value) return "Role is required";
    if (!["user", "admin"].includes(value)) return "Invalid role selected";
    return "";
  };

  const isFormValid = () => {
    const firstNameError = validateFirstName(firstName);
    const lastNameError = validateLastName(lastName);
    const emailError = validateEmail(email);
    const phoneNumberError = validatePhoneNumber(phoneNumber);
    const passwordError = validatePassword(password);
    const passwordValid = isEdit ? (password ? !passwordError : true) : (!passwordError && password);

    const textFieldsValid = !firstNameError && !lastNameError && !emailError && !phoneNumberError && passwordValid;

    // ✅ In edit mode, allow update if text fields are valid OR profile picture was changed
    if (isEdit) {
      const hasProfilePictureChange = profilePicture && typeof profilePicture !== 'string';
      return textFieldsValid || hasProfilePictureChange;
    }

    // In create mode: all fields required
    return textFieldsValid;
  };

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid());
    }
  }, [firstName, lastName, email, phoneNumber, password, isEdit, onValidationChange, profilePicture]);

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    let error = "";
    switch (field) {
      case "firstName":
        error = validateFirstName(firstName);
        break;
      case "lastName":
        error = validateLastName(lastName);
        break;
      case "email":
        error = validateEmail(email);
        break;
      case "phoneNumber":
        error = validatePhoneNumber(phoneNumber);
        break;
      case "password":
        error = validatePassword(password);
        break;
      case "role":
        error = validateRole(role);
        break;
      default:
        break;
    }
    setErrors({ ...errors, [field]: error });
  };

  const openPicker = () => fileRef.current?.click();

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file (JPEG, PNG, GIF, etc.)");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("File size too large. Please select an image smaller than 5MB.");
        return;
      }
      setLogoName(file.name);
      setLogo(URL.createObjectURL(file));
      setProfilePicture(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file (JPEG, PNG, GIF, etc.)");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("File size too large. Please select an image smaller than 5MB.");
        return;
      }
      setLogoName(file.name);
      setLogo(URL.createObjectURL(file));
      setProfilePicture(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <Box component="form">
      <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", padding: { xs: "18px", sm: "20px", lg: "25px" }, }} className="rmui-card">
        <Box sx={{ mb: "25px" }}>
          <Typography variant="h3" sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700 }} className="text-black">
            User Information
          </Typography>
        </Box>
        <Grid container spacing={2} columnSpacing={{ xs: 1, sm: 2, md: 2, lg: 3 }}>
          <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}>
            <input type="file" ref={fileRef} onChange={onFileChange} accept="image/*" style={{ display: "none" }} />
            <Box onClick={openPicker} onDrop={handleDrop} onDragOver={handleDragOver} sx={{ border: "2px dashed #ccc", borderRadius: 2, p: 3, textAlign: "center", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 220, "&:hover": { borderColor: "primary.main", backgroundColor: "#fafafa", }, }}>
              {logo && !imageError ? (
                <Avatar src={logo} alt="Profile Picture" sx={{ width: 100, height: 100, mb: 2, }} slotProps={{ img: { onError: () => setImageError(true) } }} />
              ) : (
                <Avatar sx={{ width: 100, height: 100, mb: 2, bgcolor: "#f0f0f0", }}>
                  <PersonIcon sx={{ fontSize: 50, color: "primary.main" }} />
                </Avatar>
              )}
              <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                Click or drag to upload profile picture
              </Typography>
              <Button variant="contained" color="primary" startIcon={<CloudUploadIcon />} sx={{ textTransform: "none", borderRadius: "7px" }} onClick={(e) => { e.stopPropagation(); openPicker(); }}>
                Upload
              </Button>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}>
            {/* Edit mode: Show First Name, Last Name, Email, Password (optional) */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography component="label" sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }} className="text-black">
                  First Name <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField label="Enter First Name" variant="filled" required value={firstName} onChange={(e) => { setFirstName(e.target.value); if (touched.firstName) setErrors({ ...errors, firstName: validateFirstName(e.target.value) }); }} onBlur={() => handleBlur("firstName")} error={touched.firstName && !!errors.firstName} helperText={touched.firstName && errors.firstName} sx={{ "& .MuiInputBase-root": { border: `1px solid ${touched.firstName && errors.firstName ? "#d32f2f" : "#D5D9E2"}`, backgroundColor: "#fff", borderRadius: "7px", }, "& .MuiInputBase-root::before": { border: "none" }, "& .MuiInputBase-root:hover::before": { border: "none" }, }} />
              </FormControl>
            </Box>
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography component="label" sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }} className="text-black">
                  Last Name <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField label="Enter Last Name" variant="filled" required value={lastName} onChange={(e) => { setLastName(e.target.value); if (touched.lastName) setErrors({ ...errors, lastName: validateLastName(e.target.value) }); }} onBlur={() => handleBlur("lastName")} error={touched.lastName && !!errors.lastName} helperText={touched.lastName && errors.lastName} sx={{ "& .MuiInputBase-root": { border: `1px solid ${touched.lastName && errors.lastName ? "#d32f2f" : "#D5D9E2"}`, backgroundColor: "#fff", borderRadius: "7px", }, "& .MuiInputBase-root::before": { border: "none" }, "& .MuiInputBase-root:hover::before": { border: "none" }, }} />
              </FormControl>
            </Box>
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography component="label" sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }} className="text-black">
                  User Email ID <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField label="Enter Email" variant="filled" type="email" required value={email} onChange={(e) => { setEmail(e.target.value); if (touched.email) setErrors({ ...errors, email: validateEmail(e.target.value) }); }} onBlur={() => handleBlur("email")} error={touched.email && !!errors.email} helperText={touched.email && errors.email} sx={{ "& .MuiInputBase-root": { border: `1px solid ${touched.email && errors.email ? "#d32f2f" : "#D5D9E2"}`, backgroundColor: "#fff", borderRadius: "7px", }, "& .MuiInputBase-root::before": { border: "none" }, "& .MuiInputBase-root:hover::before": { border: "none" }, }} />
              </FormControl>
            </Box>
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography component="label" sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }} className="text-black">
                  Phone Number <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField label="Enter Phone Number" variant="filled" required value={phoneNumber} onChange={(e) => { setPhoneNumber(e.target.value); if (touched.phoneNumber) setErrors({ ...errors, phoneNumber: validatePhoneNumber(e.target.value) }); }} onBlur={() => handleBlur("phoneNumber")} error={touched.phoneNumber && !!errors.phoneNumber} helperText={touched.phoneNumber && errors.phoneNumber} sx={{ "& .MuiInputBase-root": { border: `1px solid ${touched.phoneNumber && errors.phoneNumber ? "#d32f2f" : "#D5D9E2"}`, backgroundColor: "#fff", borderRadius: "7px", }, "& .MuiInputBase-root::before": { border: "none" }, "& .MuiInputBase-root:hover::before": { border: "none" }, }} />
              </FormControl>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography component="label" sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }} className="text-black">
                Security & Password {!isEdit && <span style={{ color: "red" }}>*</span>}
              </Typography>
              
              {!isEdit ? (
                /* Create Mode: Standard Password Field */
                <FormControl fullWidth>
                  <TextField 
                    label="Enter Password" 
                    variant="filled" 
                    type={showPassword ? "text" : "password"} 
                    required 
                    value={password} 
                    onChange={(e) => { setPassword(e.target.value); if (touched.password) setErrors({ ...errors, password: validatePassword(e.target.value) }); }} 
                    onBlur={() => handleBlur("password")} 
                    error={touched.password && !!errors.password} 
                    helperText={touched.password ? errors.password || "Minimum 6 characters" : "Minimum 6 characters"} 
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClickTogglePassword} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiInputBase-root": { border: `1px solid ${touched.password && errors.password ? "#d32f2f" : "#D5D9E2"}`, backgroundColor: "#fff", borderRadius: "7px", }, "& .MuiInputBase-root::before": { border: "none" }, "& .MuiInputBase-root:hover::before": { border: "none" }, }} 
                  />
                </FormControl>
              ) : (
                /* Edit Mode: Smart Password Change Button + Modal */
                <Box>
                  {password ? (
                    <Alert 
                      severity="success" 
                      icon={<CheckCircleIcon fontSize="inherit" />}
                      sx={{ 
                        borderRadius: "7px", 
                        mb: 1,
                        "& .MuiAlert-message": { width: "100%" }
                      }}
                      action={
                        <Button color="inherit" size="small" onClick={() => setPassword("")}>
                          Reset
                        </Button>
                      }
                    >
                      New password will be applied on save.
                    </Alert>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<LockResetIcon />}
                      fullWidth
                      onClick={handleOpenPasswordModal}
                      sx={{
                        py: "12px",
                        borderRadius: "7px",
                        textTransform: "none",
                        fontWeight: 600,
                        borderStyle: "dashed",
                        borderColor: "primary.main",
                        "&:hover": {
                          borderStyle: "dashed",
                          backgroundColor: "rgba(63, 81, 181, 0.04)"
                        }
                      }}
                    >
                      Change User Password
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            {/* Password Change Modal */}
            <Dialog open={openPasswordModal} onClose={handleClosePasswordModal} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Change User Password</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Enter a new password for this user. You will need to confirm the change by clicking Update on the main form.
                </Typography>
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showTempPassword ? "text" : "password"}
                    variant="outlined"
                    value={tempPassword}
                    onChange={(e) => setTempPassword(e.target.value)}
                    error={tempPassword.length > 0 && !isPasswordLengthValid}
                    helperText={tempPassword.length > 0 && !isPasswordLengthValid ? "Minimum 6 characters required" : ""}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowTempPassword(!showTempPassword)} edge="end">
                            {showTempPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showTempConfirmPassword ? "text" : "password"}
                    variant="outlined"
                    value={tempConfirmPassword}
                    onChange={(e) => setTempConfirmPassword(e.target.value)}
                    error={tempConfirmPassword.length > 0 && !isPasswordMatch}
                    helperText={tempConfirmPassword.length > 0 && !isPasswordMatch ? "Passwords do not match" : ""}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowTempConfirmPassword(!showTempConfirmPassword)} edge="end">
                            {showTempConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleClosePasswordModal} color="inherit" sx={{ textTransform: "none", fontWeight: 600 }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleConfirmPasswordChange} 
                  variant="contained" 
                  disabled={!isPasswordChangeValid}
                  sx={{ textTransform: "none", fontWeight: 600, px: 3, borderRadius: "7px" }}
                >
                  Apply Change
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default UserInfo;

